import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { Typography } from "@insite/mobius/index";
import Modal from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductAddToCartButton from "@rightstore/Overrides/Components/ProductAddToCartButton";
import QuickFreeOrderButton from "@rightstore/Overrides/Components/QuickFreeOrderButton";
import QuoteQuickRequestButton from "@rightstore/Overrides/Components/QuoteQuickRequestButton";
import getSignInUrl from "@rightstore/Utilities/getSignInUrl";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);

    return {
        currentLocation: getLocation(state),
        currentUserIsGuest: !state.context.session.isAuthenticated,
        isReadOnly: isAuthenticated && state.context.session.userRoles.includes("ReadOnly"),
        isTopup: isAuthenticated && state.context.session.userRoles.includes("TopUp"),
        roles: state.context.session.userRoles,
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        subscription: state.pages.productDetails.subscription,
        variantProductState,
    }
};

const mapDispatchToProps = {};

interface OwnProps {
    isInCart?: boolean | null;
    cart?: CartModel;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps>

export interface GoToCheckoutReviewAndSubmitButtonStyles {
    button?: ButtonPresentationProps;
    modalButtonWrapper?: InjectableCss;
    modalButtonWrapperForQuickQuote?: InjectableCss;
    quickRequestForQuoteButton?: ButtonPresentationProps;
}

const goToCheckoutReviewAndSubmitButtonStyles: GoToCheckoutReviewAndSubmitButtonStyles = {
    button: {
        css: css`  
            background: ${getColor("info")};      
            border: 1px solid ${getColor("info")};
            border-radius: 6px;
            padding: 12px 32px;
            width: 100%;
            line-height: 24px;        
            &:focus {
                outline-color: transparent;
            }
            &:not([disabled]):hover {
                background: ${getColor("common.background")};
                color: ${getColor("info")};
                border: 1px solid ${getColor("info")};
            }
            &:not([disabled]):hover span {\
                color: ${getColor("info")};
            }
        `,
    },
    modalButtonWrapper: {
        css: css`  
            padding-top: 20px;
            display: flex;
            width: 100%;
            justify-content: flex-end;
            gap: 8px;
        `,
    },
    modalButtonWrapperForQuickQuote: {
        css: css`  
            padding-top: 20px;
            display: flex;
            width: 100%;
            justify-content: flex-end;
            gap: 8px;
        `,
    },
    quickRequestForQuoteButton: {
        variant: "primary",
        css: css`
            background: ${getColor("info")};
            border: 1px solid ${getColor("info")};
            border-radius: 6px;
            padding: 12px 32px;
            line-height: 0;
            &:focus {
                outline-color: transparent;
            }
            &:not([disabled]):hover {
                background: ${getColor("common.background")};
                border: 1px solid ${getColor("info")};
                color: ${getColor("info")};
            }
            &:not([disabled]):hover span {
                color: ${getColor("info")};
            }
            width: 100%;
        `,
    },
}

const GoToCheckoutReviewAndSubmitButton: React.FC<Props> = ({
    productContext,
    currentUserIsGuest,
    history,
    currentLocation,
    isInCart,
    cart,
    isReadOnly,
    isTopup,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    variantProductState,
}) => {
    const styles = goToCheckoutReviewAndSubmitButtonStyles
    const { product } = productContext
    const isQuote = product.quoteRequired
    const [openModal, setOpenModal] = useState<boolean>(false)
    const cartProductName = cart?.cartLines && cart?.cartLines?.length > 0 ? cart?.cartLines?.[0].shortDescription : ""
    const isTopupRole = isTopup;
    const isTopupProduct = product.productNumber.toLowerCase().startsWith("topup");
    const productTypeButtonText = variantProductState?.value?.properties?.productTypeButtonText || product.properties.productTypeButtonText;
    const quoteProductTypeButtonText = variantProductState?.value?.properties?.quoteProductTypeText || product.properties.quoteProductTypeText;
    const CanRequestQuickQuote = variantProductState?.value?.properties?.canRequestQuickQuote || product.properties.canRequestQuickQuote;
    const freeProductQuickFlowEnabled = variantProductState?.value?.properties?.canRequestQuickFreeProduct || product.properties?.canRequestQuickFreeProduct;
    const isFreeProduct = product.unitListPrice === 0;
    const useFreeQuickFlow = isFreeProduct && freeProductQuickFlowEnabled === "true";
    const isPlaceholderProduct = product.properties?.productIsPlaceholder === "true";
    const placeholderButtonUrl = product.properties?.placeholderProductButtonUrl && product.properties?.placeholderProductButtonUrl;

    const openModalEvent = () => {
        setOpenModal(true)
    }

    const closeModalEvent = () => {
        setOpenModal(false)
    }

    if (isReadOnly) {
        return null
    }

    if (!isTopupRole && isTopupProduct) {
        return null
    }

    const [signInUrl, setSignInUrl] = useState<string>("");
    useEffect(() => {
        const tryGetAndSetSignInUrl = async () => {
            setSignInUrl(await getSignInUrl({ currentLocation, selectedVariant: variantProductState }));
        }

        tryGetAndSetSignInUrl();
    }, [currentLocation, variantProductState]);

    const handleSignin = () => {
        if (window) {
            window.location.replace(`${signInUrl}`);
        }
    }

    const handlePlaceholderClick = () => {
        if (window) {
            window.open(placeholderButtonUrl, '_blank');
        }
    }

    const quoteText = isQuote && (quoteProductTypeButtonText || translate("Request Now"));
    const placeholderProductText = isPlaceholderProduct && (productTypeButtonText || translate("Get Access"));

    if (currentUserIsGuest) {
        return (
            <>
                <Button
                    {...styles.button}
                    onClick={handleSignin}
                >
                    {productTypeButtonText || quoteText || placeholderProductText || translate("Get Report")}
                </Button>
            </>
        );
    }

    if (isPlaceholderProduct) {
        return (
            <Button
                {...styles.button}
                onClick={() => handlePlaceholderClick()}
            >
                {productTypeButtonText || translate("Get Access")}
            </Button>
        )
    }

    // Quick Request for Quote flow
    if (isQuote && CanRequestQuickQuote === "true") {
        return (
            <>
                {!isInCart ? (
                    <QuoteQuickRequestButton
                        labelOverride={quoteProductTypeButtonText || translate("Request Now")}
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                        setOpenModal={setOpenModal}
                        extendedStyles={styles.button}
                    />
                ) : (
                    <Button
                        {...styles.button}
                        onClick={() => openModalEvent()}
                    >
                        {quoteProductTypeButtonText || translate("Request Now")}
                    </Button>
                )}
                {openModal && (
                    <Modal
                        isOpen={openModal}
                        headline={translate("Another product already in the cart")}
                        handleClose={closeModalEvent}
                        sizeVariant="medium"
                        alert={true}
                    >
                        <Typography>{translate(`Start with a new request?`)}</Typography>
                        <br />
                        <Typography>{translate(`Your cart containing ${cartProductName} will be cleared.`)}</Typography>
                        <StyledWrapper {...styles.modalButtonWrapperForQuickQuote}>
                            <Button
                                variant="secondary"
                                onClick={() => closeModalEvent()}
                            >
                                {translate("Cancel")}
                            </Button>
                            <QuoteQuickRequestButton
                                setOpenModal={setOpenModal}
                                labelOverride={translate("Continue")}
                                configurationSelection={configurationSelection}
                                configurationCompleted={configurationCompleted}
                                variantSelectionCompleted={variantSelectionCompleted}
                                subscription={subscription}
                            />
                        </StyledWrapper>
                    </Modal>
                )}
            </>
        )
    }

    // Quick free product order flow
    if (useFreeQuickFlow && !isQuote) {
        return (
            <>
                {!isInCart ? (
                    <QuickFreeOrderButton
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                        labelOverride={productTypeButtonText || translate('Get Report')}
                        extendedStyles={styles.button}
                        setOpenModal={setOpenModal}
                    />
                ) : (
                    <Button
                        {...styles.button}
                        onClick={() => openModalEvent()}
                    >
                        {quoteProductTypeButtonText || translate("Request Now")}
                    </Button>
                )}
                {openModal && (
                    <Modal
                        isOpen={openModal}
                        headline={translate("Another product already in the cart")}
                        handleClose={closeModalEvent}
                        sizeVariant="medium"
                        alert={true}
                    >
                        <Typography>{translate(`Start with a new request?`)}</Typography>
                        <br />
                        <Typography>{translate(`Your cart containing ${cartProductName} will be cleared.`)}</Typography>
                        <StyledWrapper {...styles.modalButtonWrapperForQuickQuote}>
                            <Button
                                variant="secondary"
                                onClick={() => closeModalEvent()}
                            >
                                {translate("Cancel")}
                            </Button>
                            <QuickFreeOrderButton
                                configurationSelection={configurationSelection}
                                configurationCompleted={configurationCompleted}
                                variantSelectionCompleted={variantSelectionCompleted}
                                subscription={subscription}
                                labelOverride={productTypeButtonText || translate('Get Report')}
                                extendedStyles={styles.button}
                                setOpenModal={setOpenModal}
                            />
                        </StyledWrapper>
                    </Modal>
                )}
            </>
        )
    }

    // Normal order flow
    return (
        <>
            {!isInCart ? (
                <ProductAddToCartButton
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    subscription={subscription}
                    labelOverride={productTypeButtonText || (isQuote ? (quoteProductTypeButtonText || translate("Request Now")) : translate('Get Report'))}
                    extendedStyles={styles.button}
                />
            ) : (
                <Button
                    {...styles.button}
                    onClick={() => openModalEvent()}
                >
                    {productTypeButtonText || (isQuote ? (quoteProductTypeButtonText || translate("Request Now")) : translate('Get Report'))
                    }
                </Button>
            )}
            {openModal && (
                <Modal
                    isOpen={openModal}
                    headline={translate("Another product already in the cart")}
                    handleClose={closeModalEvent}
                    sizeVariant="medium"
                    alert={true}
                >
                    <Typography>{translate(`Start with a new purchase?`)}</Typography>
                    <br />
                    <Typography>{translate(`Your cart containing ${cartProductName} will be cleared.`)}</Typography>
                    <StyledWrapper {...styles.modalButtonWrapper}>
                        <Button
                            variant="secondary"
                            onClick={() => closeModalEvent()}
                        >
                            {translate("Cancel")}
                        </Button>
                        <ProductAddToCartButton
                            labelOverride={translate("Continue")}
                            configurationSelection={configurationSelection}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                            subscription={subscription}
                        />
                    </StyledWrapper>
                </Modal>
            )}
        </>
    )
};

export default connect(mapStateToProps)(withHistory(withProductContext(GoToCheckoutReviewAndSubmitButton)));

